/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$table-renderer-column-spacing: 32px !default;
$table-renderer-column-spacing-mobile: 16px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

.#{$prefix}table {
  color: v(paragraphColor);
  background-color: v(tableBackgroundColor);

  * {
    box-sizing: border-box;
  }

  a {
    text-decoration: underline;
    cursor: pointer;
    font-weight: bold;
    color: v(paragraphColor);
  }

  sup {
    vertical-align: super;
    line-height: 0;
    top: -1px;
    font-size: 8px;
  }

  .#{$prefix}table-footer {
    position: relative;
    width: 100%;
    margin-top: 32px;
  }

  .#{$prefix}table-content {
    position: relative;
    width: 100%;
    @include eon-typography("copy-small");
    text-align: left;
    line-height: 18px;

    table {
      position: relative;
      width: 100%;
      border-collapse: collapse;
      font-variant-numeric: tabular-nums;

      td,
      th {
        position: relative;
        padding-top: 9px;
        padding-bottom: 9px;
        padding-right: $table-renderer-column-spacing;

        .#{$prefix}highlight {
          color: v(tableHighlightColor);
        }

        .#{$prefix}highlight-value {
          color: v(tableHighlightColor);
        }

        &:after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: calc(100% - #{$table-renderer-column-spacing});
          height: 1px;
          background-color: v(tableBorderColor);
        }

        &:last-child {
          padding-right: 0;

          &:after {
            width: calc(100%);
          }
        }
      }

      th {
        font-weight: 700;

        &:after {
          height: 2px;
        }
      }

      tfoot {
        td {
          font-weight: 700;

          &:after {
            height: 2px;
          }
        }
      }
    }

    &.#{$prefix}highlight-last-row {
      &.#{$prefix}no-tfoot {
        table {
          tbody {
            tr:last-child {
              td {
                font-weight: 700;

                &:after {
                  height: 2px;
                }
              }
            }
          }
        }
      }
    }

    &.#{$prefix}highlight-last-column {
      table {
        tr {
          th:last-child {
            &:after {
              background-color: v(tableHighlightColor);
            }
          }

          td:last-child {
            &:after {
              background-color: v(tableHighlightColor);
            }
          }
        }
      }
    }

    &.#{$prefix}size-large {
      @include eon-typography("copy");
      line-height: 24px;

      sup {
        font-size: 12px;
      }

      table {
        td,
        th {
          padding-top: 10px;
          padding-bottom: 10px;
        }
      }
    }
  }
}

@include for-size(tablet) {
  .#{$prefix}table {
    .#{$prefix}table-content {
      &.#{$prefix}size-large {
        @include eon-typography("copy-small");
        line-height: 18px;

        sup {
          font-size: 8px;
        }

        table {
          td,
          th {
            padding-top: 9px;
            padding-bottom: 9px;
          }
        }
      }
    }
  }
}

/*
Responsive Data Table:
*/
@include for-size(phone) {
  .#{$prefix}table {
    .#{$prefix}table-content:not(.#{$prefix}dont-stack) {
      /* Force table to not be like tables anymore */
      table,
      thead,
      tbody,
      tfoot,
      th,
      td,
      tr {
        display: block;
      }

      table {
        width: auto !important;

        /* Hide table headers (but not display: none;, for accessibility) */
        thead tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }

        tr {
          padding-top: 25px;
          padding-bottom: 25px;
        }

        tr:nth-child(even) {
          width: calc(100% + 48px);
          transform: translateX(-24px);
          padding-right: 24px;
          padding-left: 24px;
          background-color: v(tableAlternateBackgroundColor);
        }

        td {
          /* Behave  like a "row" */
          position: relative;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          padding-right: 0;
          display: flex;
          justify-content: stretch;
          align-items: flex-end;
          height: 100%;
          min-height: 38px;

          .#{$prefix}cell-wrapper {
            padding-top: 10px;
            padding-bottom: 12px;
            text-align: left;
          }

          &:after {
            left: unset;
            right: 0;
            width: 60%;
          }

          &:last-child {
            &:after {
              width: 60%;
            }
          }
        }

        tfoot {
          td {
            font-weight: normal;
            border: none;
          }
        }

        td:before {
          /* Now like a table header */
          content: attr(data-title);
          position: relative;
          width: calc(40% - #{$table-renderer-column-spacing-mobile});
          height: auto;
          margin-right: #{$table-renderer-column-spacing-mobile};
          padding: 10px 0px 10px 0px;
          border-bottom: 2px solid v(tableBorderColor);
          font-weight: 700;
          align-self: stretch;
          flex-shrink: 0;
          box-sizing: border-box;
          text-align: left;
          align-items: flex-end;
          display: flex;
        }
      }

      &.#{$prefix}highlight-last-column {
        table {
          tr {
            td:last-child {
              div {
                border-color: v(tableHighlightColor);
              }
            }
          }
        }
      }

      &.#{$prefix}highlight-last-row {
        table {
          tr:last-child {
            td {
              border-color: inherit;
              border-bottom: inherit;
              font-weight: inherit;
            }
          }
        }
      }

      &.#{$prefix}no-thead {
        tr:first-child {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }

        tr:nth-child(even) {
          width: auto;
          transform: unset;
          padding-right: inherit;
          padding-left: inherit;
          background-color: inherit;
        }

        tr:nth-child(odd) {
          width: calc(100% + 48px);
          transform: translateX(-24px);
          padding-right: 24px;
          padding-left: 24px;
          background-color: v(tableAlternateBackgroundColor);
        }
      }

      &.#{$prefix}size-large {
        @include eon-typography("copy-small");
        line-height: 18px;

        sup {
          font-size: 8px;
        }

        table {
          td {
            .#{$prefix}cell-wrapper {
              padding-top: 9px;
              padding-bottom: 9px;
            }
          }
        }
      }
    }

    .#{$prefix}dont-stack {
      width: auto;
      overflow-x: auto;
      white-space: nowrap;
      padding-bottom: 15px;
    }
  }
}

.#{$prefix}highlight-cell {
  &:after {
    background-color: v(tableHighlightColor) !important;
  }
}

.#{$prefix}highlight-border {
  &:after {
    height: 2px !important;
  }
}

::slotted(eon-ui-table-footnote) {
  color: v(paragraphColor);
}
